import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
} from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";
import { useFilaIntegracaoEvents } from "../../../hooks/useFilaIntegracaoEvents";
import { FILA_INTEGRACAO_SSE_QUERY_KEYS } from "../../../constants/rankingEventQueryKeys";
import {
  FilaBebidaItem,
  filaBebidasService,
} from "../../../services/filaBebidasService";
import { BrazilDate } from "../../../utils/BrazilDate";
import { broadcastDashboardRefresh } from "../../../utils/dashboardRefreshBroadcast";
import { BEBIDA_COLUNAS, BebidaStatusColuna } from "../constants/bebidas";
import {
  agruparItensFilaBebidas,
  FilaBebidaItemAgrupado,
} from "../utils/agruparItensFilaBebidas";
import {
  agruparBebidasPorMesa,
  BebidaMesaTicket,
} from "../utils/agruparBebidasPorMesa";

interface ProducaoBebidasContextData {
  items: FilaBebidaItem[];
  colunas: Record<BebidaStatusColuna, FilaBebidaItemAgrupado[]>;
  mesasPending: BebidaMesaTicket[];
  mesasDone: BebidaMesaTicket[];
  loading: boolean;
  refreshing: boolean;
  busyIds: Set<number>;
  refreshNow: () => Promise<void>;
  handleFinalizar: (ids: number[]) => Promise<void>;
  totalAtivos: number;
}

const ProducaoBebidasContext = createContext<
  ProducaoBebidasContextData | undefined
>(undefined);

export const ProducaoBebidasProvider: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  const queryClient = useQueryClient();
  const todayISO = useMemo(() => BrazilDate.todayISO(), []);

  useFilaIntegracaoEvents(1, {
    queryKeys: FILA_INTEGRACAO_SSE_QUERY_KEYS.filaBebidas,
  });

  const { data, isLoading, isFetching, refetch } = useQuery({
    queryKey: ["fila-bebidas", "producao", todayISO],
    queryFn: () =>
      filaBebidasService.list({
        data: todayISO,
        page: 1,
        limit: 500,
      }),
    refetchInterval: 30_000,
  });

  const items = useMemo(
    () => (data?.data ?? []).filter((i) => i.status !== "cancelled"),
    [data],
  );

  const finalizarMutation = useMutation({
    mutationFn: async (ids: number[]) => {
      await Promise.all(ids.map((id) => filaBebidasService.finalizar(id)));
      return ids;
    },
    onSuccess: (ids) => {
      broadcastDashboardRefresh();
      toast.success(
        ids.length > 1 ? "Bebidas liberadas" : "Bebida finalizada",
      );
      queryClient.invalidateQueries({ queryKey: ["fila-bebidas"] });
    },
    onError: () => toast.error("Erro ao finalizar bebida"),
  });

  const busyIds = useMemo(() => {
    const ids = finalizarMutation.isPending
      ? (finalizarMutation.variables ?? [])
      : [];
    return new Set(ids);
  }, [finalizarMutation.isPending, finalizarMutation.variables]);

  const refreshNow = useCallback(async () => {
    await refetch();
  }, [refetch]);

  const handleFinalizar = useCallback(
    async (ids: number[]) => {
      if (ids.length === 0) return;
      await finalizarMutation.mutateAsync(ids);
    },
    [finalizarMutation],
  );

  const colunas = useMemo(() => {
    const grouped: Record<BebidaStatusColuna, FilaBebidaItem[]> = {
      pending: [],
      done: [],
    };
    for (const item of items) {
      if (item.status === "pending" || item.status === "in_progress") {
        grouped.pending.push(item);
      } else if (item.status === "done") {
        grouped.done.push(item);
      }
    }
    return {
      pending: agruparItensFilaBebidas(grouped.pending),
      done: agruparItensFilaBebidas(grouped.done),
    };
  }, [items]);

  const mesasPending = useMemo(
    () => agruparBebidasPorMesa(colunas.pending),
    [colunas.pending],
  );

  const mesasDone = useMemo(
    () => agruparBebidasPorMesa(colunas.done),
    [colunas.done],
  );

  const totalAtivos = mesasPending.reduce(
    (acc, t) => acc + t.allItemIds.length,
    0,
  );

  const value = useMemo(
    () => ({
      items,
      colunas,
      mesasPending,
      mesasDone,
      loading: isLoading,
      refreshing: isFetching && !isLoading,
      busyIds,
      refreshNow,
      handleFinalizar,
      totalAtivos,
    }),
    [
      items,
      colunas,
      mesasPending,
      mesasDone,
      isLoading,
      isFetching,
      busyIds,
      refreshNow,
      handleFinalizar,
      totalAtivos,
    ],
  );

  return (
    <ProducaoBebidasContext.Provider value={value}>
      {children}
    </ProducaoBebidasContext.Provider>
  );
};

export const useProducaoBebidas = () => {
  const context = useContext(ProducaoBebidasContext);
  if (!context) {
    throw new Error(
      "useProducaoBebidas must be used within ProducaoBebidasProvider",
    );
  }
  return context;
};

export { BEBIDA_COLUNAS };
